import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, Video, Film, Share2, Sparkles, Zap } from 'lucide-react';

// Hook per rispettare prefers-reduced-motion
const usePrefersReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(false);

  React.useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return prefersReducedMotion;
};

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  color?: 'primary' | 'secondary' | 'white';
  className?: string;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  color = 'primary',
  className = ''
}) => {
  const prefersReducedMotion = usePrefersReducedMotion();

  // Responsive sizes: larger on mobile for better touch visibility
  const sizeClasses = {
    sm: 'w-4 h-4 sm:w-4 sm:h-4',
    md: 'w-6 h-6 sm:w-6 sm:h-6 md:w-7 md:h-7',
    lg: 'w-10 h-10 sm:w-8 sm:h-8 md:w-10 md:h-10'
  };

  const colorClasses = {
    primary: 'border-yellow-400 border-t-transparent',
    secondary: 'border-gray-400 border-t-transparent',
    white: 'border-white border-t-transparent'
  };

  return (
    <div
      className={`loading-spinner border-2 rounded-full ${prefersReducedMotion ? '' : 'animate-spin'} ${sizeClasses[size]} ${colorClasses[color]} ${className}`}
      role="status"
      aria-label="Loading"
    >
      <span className="sr-only">Loading...</span>
    </div>
  );
};

interface LoadingDotsProps {
  size?: 'sm' | 'md' | 'lg';
  color?: 'primary' | 'secondary';
  className?: string;
}

export const LoadingDots: React.FC<LoadingDotsProps> = ({
  size = 'md',
  color = 'primary',
  className = ''
}) => {
  const prefersReducedMotion = usePrefersReducedMotion();

  // Responsive dot sizes
  const sizeClasses = {
    sm: 'w-1.5 h-1.5 sm:w-1 sm:h-1',
    md: 'w-2.5 h-2.5 sm:w-2 sm:h-2',
    lg: 'w-4 h-4 sm:w-3 sm:h-3'
  };

  const colorClasses = {
    primary: 'bg-yellow-400',
    secondary: 'bg-gray-400'
  };

  return (
    <div
      className={`loading-dots flex items-center justify-center gap-1 sm:gap-1.5 ${className}`}
      role="status"
      aria-label="Loading"
    >
      <span
        className={`${sizeClasses[size]} ${colorClasses[color]} rounded-full ${prefersReducedMotion ? 'opacity-60' : 'animate-pulse'}`}
        style={{ animationDelay: '0s' }}
      />
      <span
        className={`${sizeClasses[size]} ${colorClasses[color]} rounded-full ${prefersReducedMotion ? 'opacity-80' : 'animate-pulse'}`}
        style={{ animationDelay: '0.2s' }}
      />
      <span
        className={`${sizeClasses[size]} ${colorClasses[color]} rounded-full ${prefersReducedMotion ? '' : 'animate-pulse'}`}
        style={{ animationDelay: '0.4s' }}
      />
      <span className="sr-only">Loading...</span>
    </div>
  );
};

interface SkeletonProps {
  width?: string;
  height?: string;
  className?: string;
  rounded?: boolean;
  responsive?: boolean;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  width = 'w-full',
  height = 'h-4',
  className = '',
  rounded = true,
  responsive = true
}) => {
  const prefersReducedMotion = usePrefersReducedMotion();

  // Responsive height adjustments
  const responsiveHeight = responsive ? `${height} sm:${height}` : height;

  return (
    <div
      className={`loading-skeleton bg-gray-200 dark:bg-gray-700 ${prefersReducedMotion ? 'opacity-60' : 'animate-pulse'} ${width} ${responsiveHeight} ${rounded ? 'rounded-md sm:rounded' : ''} ${className}`}
      role="status"
      aria-label="Loading content"
    >
      <span className="sr-only">Loading content...</span>
    </div>
  );
};

interface LoadingButtonProps {
  children: React.ReactNode;
  loading: boolean;
  disabled?: boolean;
  className?: string;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
}

export const LoadingButton: React.FC<LoadingButtonProps> = ({
  children,
  loading,
  disabled = false,
  className = '',
  onClick,
  type = 'button'
}) => {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`
        relative inline-flex items-center justify-center
        px-4 py-3 sm:px-4 sm:py-2
        min-h-[44px] sm:min-h-[40px]
        rounded-xl sm:rounded-lg font-medium
        transition-all duration-200
        touch-manipulation
        active:scale-95 sm:active:scale-100
        ${loading ? 'cursor-not-allowed opacity-70' : ''}
        ${className}
      `}
      aria-disabled={disabled || loading}
      aria-busy={loading}
    >
      {loading && (
        <LoadingSpinner
          size="sm"
          color="white"
          className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2"
        />
      )}
      <span className={`text-sm sm:text-base ${loading ? 'opacity-0' : 'opacity-100'}`}>
        {children}
      </span>
    </button>
  );
};

interface LoadingOverlayProps {
  show: boolean;
  message?: string;
  className?: string;
}

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  show,
  message = 'Loading...',
  className = ''
}) => {
  const prefersReducedMotion = usePrefersReducedMotion();

  if (!show) return null;

  return (
    <div
      className={`
        fixed inset-0 bg-black/50 backdrop-blur-sm
        flex items-center justify-center z-50
        p-4 sm:p-6
        ${className}
      `}
      role="dialog"
      aria-modal="true"
      aria-label="Loading"
    >
      <div
        className={`
          bg-white dark:bg-gray-800 rounded-2xl sm:rounded-lg
          p-6 sm:p-6 md:p-8 shadow-xl
          max-w-[90vw] sm:max-w-sm w-full
          mx-4 sm:mx-auto
          ${prefersReducedMotion ? '' : 'animate-fade-in'}
        `}
      >
        <div className="flex flex-col items-center space-y-4 sm:space-y-4">
          <LoadingSpinner size="lg" />
          <p className="text-gray-700 dark:text-gray-300 font-medium text-center text-base sm:text-base">
            {message}
          </p>
        </div>
      </div>
    </div>
  );
};

// Card skeleton for loading states with personality
export const CardSkeleton: React.FC<{ className?: string }> = ({ className = '' }) => {
  const prefersReducedMotion = usePrefersReducedMotion();

  return (
    <motion.div
      className={`glass-card p-4 sm:p-6 space-y-3 sm:space-y-4 ${className}`}
      initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: prefersReducedMotion ? 0 : 0.3 }}
    >
      <div className="flex items-center space-x-3 mb-3 sm:mb-4">
        <motion.div
          className="w-10 h-10 sm:w-10 sm:h-10 bg-gradient-to-r from-yellow-200 to-yellow-300 rounded-full flex items-center justify-center flex-shrink-0"
          animate={prefersReducedMotion ? {} : { rotate: [0, 360] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
        >
          <Video className="w-5 h-5 sm:w-5 sm:h-5 text-yellow-600" />
        </motion.div>
        <div className="flex-1 min-w-0">
          <Skeleton width="w-3/4" height="h-5 sm:h-6" />
          <Skeleton width="w-1/2" height="h-3 sm:h-4" className="mt-1.5 sm:mt-1" />
        </div>
      </div>
      <div className="space-y-2 sm:space-y-2">
        <Skeleton width="w-full" height="h-3 sm:h-4" />
        <Skeleton width="w-5/6" height="h-3 sm:h-4" />
        <Skeleton width="w-4/6" height="h-3 sm:h-4" />
      </div>
      <div className="flex flex-wrap gap-2 sm:space-x-2 sm:gap-0">
        <Skeleton width="w-20 sm:w-20" height="h-9 sm:h-8" />
        <Skeleton width="w-24 sm:w-24" height="h-9 sm:h-8" />
      </div>
    </motion.div>
  );
};

// Creative Loading States for different contexts
export const VideoProcessingLoader: React.FC<{ message?: string }> = ({
  message = "Elaborando il tuo video..."
}) => {
  const prefersReducedMotion = usePrefersReducedMotion();

  return (
    <div className="flex flex-col items-center space-y-4 sm:space-y-4 py-6 sm:py-8 px-4">
      <div className="relative">
        <motion.div
          className="w-14 h-14 sm:w-16 sm:h-16 border-4 border-yellow-200 border-t-yellow-500 rounded-full"
          animate={prefersReducedMotion ? {} : { rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        />
        <motion.div
          className="absolute inset-0 flex items-center justify-center"
          animate={prefersReducedMotion ? {} : { scale: [1, 1.2, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <Film className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600" />
        </motion.div>
      </div>
      <div className="text-center max-w-[280px] sm:max-w-none">
        <p className="text-gray-700 dark:text-gray-300 font-medium text-sm sm:text-base">
          {message}
        </p>
        <div className="flex space-x-1.5 sm:space-x-1 mt-2 sm:mt-2 justify-center">
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="w-2.5 h-2.5 sm:w-2 sm:h-2 bg-yellow-400 rounded-full"
              animate={prefersReducedMotion ? {} : { scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
              transition={{
                duration: 1,
                repeat: Infinity,
                delay: i * 0.2
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export const ContentUploadLoader: React.FC = () => {
  const prefersReducedMotion = usePrefersReducedMotion();

  const steps = [
    { icon: Camera, label: "Caricamento", color: "text-blue-500" },
    { icon: Film, label: "Elaborazione", color: "text-purple-500" },
    { icon: Sparkles, label: "Ottimizzazione", color: "text-yellow-500" },
    { icon: Share2, label: "Finalizzazione", color: "text-green-500" }
  ];

  const [currentStep, setCurrentStep] = React.useState(0);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % steps.length);
    }, 1500);

    return () => clearInterval(interval);
  }, [steps.length]);

  return (
    <div className="flex flex-col items-center space-y-4 sm:space-y-6 py-6 sm:py-8 px-4">
      {/* Mobile: 2x2 grid, Desktop: row */}
      <div className="grid grid-cols-2 gap-3 sm:flex sm:space-x-4 sm:gap-0">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isActive = index === currentStep;
          const isPassed = index < currentStep;

          return (
            <motion.div
              key={index}
              className={`w-12 h-12 sm:w-12 sm:h-12 rounded-full flex items-center justify-center border-2 mx-auto ${
                isActive
                  ? 'border-yellow-400 bg-yellow-50 dark:bg-yellow-900/30'
                  : isPassed
                  ? 'border-green-400 bg-green-50 dark:bg-green-900/30'
                  : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800'
              }`}
              animate={prefersReducedMotion ? {} : {
                scale: isActive ? [1, 1.2, 1] : 1,
                rotate: isActive ? [0, 360] : 0
              }}
              transition={{
                duration: isActive ? 1 : 0.3,
                repeat: isActive ? Infinity : 0
              }}
            >
              <Icon
                className={`w-5 h-5 sm:w-5 sm:h-5 ${
                  isActive
                    ? 'text-yellow-600'
                    : isPassed
                    ? 'text-green-600'
                    : 'text-gray-400'
                }`}
              />
            </motion.div>
          );
        })}
      </div>

      <div className="text-center">
        <motion.p
          key={currentStep}
          className="text-gray-700 dark:text-gray-300 font-medium text-sm sm:text-base"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: prefersReducedMotion ? 0 : 0.3 }}
        >
          {steps[currentStep].label}...
        </motion.p>
        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
          {currentStep + 1} di {steps.length} step completati
        </p>
      </div>
    </div>
  );
};

// Shimmer effect for better skeleton loading
export const ShimmerSkeleton: React.FC<SkeletonProps> = ({
  width = 'w-full',
  height = 'h-4',
  className = '',
  rounded = true
}) => {
  const prefersReducedMotion = usePrefersReducedMotion();

  return (
    <div
      className={`loading-skeleton bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 bg-[length:200%_100%] ${prefersReducedMotion ? '' : 'animate-pulse'} ${width} ${height} ${rounded ? 'rounded-md sm:rounded' : ''} ${className}`}
      style={prefersReducedMotion ? {} : {
        backgroundImage: 'linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%)',
        animation: 'shimmer 1.5s infinite'
      }}
      role="status"
      aria-label="Loading content"
    >
      <span className="sr-only">Loading content...</span>
    </div>
  );
};

// Table skeleton for loading states
export const TableSkeleton: React.FC<{ rows?: number; columns?: number }> = ({
  rows = 5,
  columns = 4
}) => {
  // On mobile, show fewer columns
  const [visibleColumns, setVisibleColumns] = React.useState(columns);

  React.useEffect(() => {
    const updateColumns = () => {
      if (window.innerWidth < 640) {
        setVisibleColumns(Math.min(2, columns));
      } else if (window.innerWidth < 768) {
        setVisibleColumns(Math.min(3, columns));
      } else {
        setVisibleColumns(columns);
      }
    };

    updateColumns();
    window.addEventListener('resize', updateColumns);
    return () => window.removeEventListener('resize', updateColumns);
  }, [columns]);

  return (
    <div className="space-y-3 sm:space-y-4 overflow-x-auto">
      {/* Header */}
      <div className="flex space-x-2 sm:space-x-4 min-w-0">
        {Array.from({ length: visibleColumns }).map((_, i) => (
          <Skeleton key={i} width="flex-1" height="h-5 sm:h-6" />
        ))}
      </div>

      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex space-x-2 sm:space-x-4 min-w-0">
          {Array.from({ length: visibleColumns }).map((_, colIndex) => (
            <Skeleton key={colIndex} width="flex-1" height="h-8 sm:h-10" />
          ))}
        </div>
      ))}
    </div>
  );
};

// Export the hook for other components
export { usePrefersReducedMotion };